import { z } from 'zod';

import type { NativeAuth } from './auth';

import type { NativeAndroidUser, NativeOptions, OktaUserRaw } from '@/types';

const androidNativeOptionsSchema = z.object({
  menu: z.boolean(),
});

const nativeAndroidUserSchema = z.object({
  accessToken: z.string(),
  userFromToken: z.custom<OktaUserRaw>(
    value => typeof value === 'object' && value !== null,
  ),
  versionInfo: z.string(),
  isMdm: z.boolean(),
  isDev: z.boolean(),
  country: z.string(),
  language: z.string(),
  selectedLocation: z.string(),
  nativeOptions: androidNativeOptionsSchema.optional(),
});

const setupNativeBindings = () => {
  const androidUser = getAndroidUser();
  const nativeAuth = getAuthFromNative(androidUser);
  const showNavbar = shouldShowNavbar(nativeAuth, androidUser);
  window.native = {
    log: (message?: unknown, ...optionalParams: unknown[]) => {
      const formattedMessage = [message, ...optionalParams]
        .map(m => {
          if (m !== null && typeof m === 'object') {
            try {
              return JSON.stringify(m);
            } catch (_) {
              return '[Circular]';
            }
          }
          if (
            typeof m === 'string' ||
            typeof m === 'number' ||
            typeof m === 'boolean' ||
            typeof m === 'bigint'
          ) {
            return String(m);
          }
          if (m === undefined) {
            return 'undefined';
          }
          if (m === null) {
            return 'null';
          }
          if (typeof m === 'symbol') {
            return m.toString();
          }
          if (typeof m === 'function') {
            return `[Function: ${m.name || 'anonymous'}]`;
          }
          return '[Unknown]';
        })
        .join(', ');
      logToNative(formattedMessage);
    },
    isNative: nativeAuth.isNative,
    platform: !nativeAuth.isNative
      ? 'web'
      : androidUser !== undefined
        ? 'android'
        : 'ios',
    accessToken: nativeAuth.accessToken,
    nativeUser: nativeAuth.nativeUser,
    showNavbar,
  };
};

const logToNative = (message?: string) => {
  window.ReactNativeWebView?.postMessage(
    JSON.stringify({
      type: '_log',
      payload: message ?? '',
    }),
  );
};

const getAndroidUser = (): NativeAndroidUser | undefined => {
  try {
    if (window.ReactNativeWebView?.injectedObjectJson) {
      const parsed: unknown = JSON.parse(
        window.ReactNativeWebView.injectedObjectJson(),
      );
      return nativeAndroidUserSchema.parse(parsed);
    }
  } catch (error) {
    console.error('Native Error parsing Android user:', error);
  }
  return undefined;
};

const getAuthFromNative = (
  androidUser: NativeAndroidUser | undefined,
): NativeAuth => {
  const user = window.userFromToken
    ? (JSON.parse(window.userFromToken) as OktaUserRaw)
    : androidUser?.userFromToken;

  const token = window.accessToken ?? androidUser?.accessToken;

  const isNative = user !== undefined && token !== undefined;

  if (isNative) {
    return {
      isNative: true,
      accessToken: token,
      nativeUser: user,
    };
  }

  return {
    isNative: false,
    accessToken: undefined,
    nativeUser: undefined,
  };
};

const nativeOptionsSchema = z.object({
  menu: z.boolean(),
});

const shouldShowNavbar = (
  nativeAuth: NativeAuth,
  androidUser: NativeAndroidUser | undefined,
): boolean => {
  if (!nativeAuth.isNative) {
    return true;
  }

  // handling how iOS RN pass options through
  if (window.nativeOptions) {
    try {
      const options: NativeOptions = nativeOptionsSchema.parse(
        JSON.parse(window.nativeOptions),
      );
      return options.menu;
    } catch (error) {
      console.error('Error parsing native options:', JSON.stringify(error));
      return false;
    }
  }

  // handling how Android RN pass options through
  if (androidUser?.nativeOptions?.menu !== undefined) {
    return androidUser.nativeOptions.menu;
  }

  // default to false for Pathway native app
  return false;
};

// called in main.tsx on import
setupNativeBindings();
